import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import { comparisons } from '../compare/registry';
import { postsTagged } from './postLookup';
import { topics } from './topics';

const SITE_URL = 'https://www.greyfielddata.com';

const tools = [
  {
    href: '/tools/ecommerce-data-stack-quiz',
    label: 'Build your ecommerce data stack',
    body: 'Ten questions about channels, team, and reporting today. Get one pick per layer, from ingestion to BI, the reasons for each, and the phase to start with.',
    tag: 'Quiz',
  },
  {
    href: '/tools/ecommerce-analytics-tool-quiz',
    label: 'Ecommerce analytics setup quiz',
    body: 'Eight questions about channels, revenue, team, and budget. Get a recommended path, from Shopify reports to a packaged tool or a warehouse build, with the reasons.',
    tag: 'Quiz',
  },
  {
    href: '/tools/bi-tool-quiz',
    label: 'BI tool quiz',
    body: 'Eight questions about your warehouse, report builders, and budget. Get a recommendation among Sigma, Power BI, Tableau, Looker, Metabase, Hex, and Omni.',
    tag: 'Quiz',
  },
  {
    href: '/tools/tableau-to-sigma-migration-assessment',
    label: 'Tableau to Sigma migration assessment',
    body: 'Describe your Tableau estate and get a readout: size, what needs rebuilding, a pilot pick, and a phase plan.',
    tag: 'Migration',
  },
  {
    href: '/tools/power-bi-to-sigma-migration-assessment',
    label: 'Power BI to Sigma migration assessment',
    body: 'Describe your Power BI estate and get a readout: size, what needs rebuilding, a pilot pick, and a phase plan.',
    tag: 'Migration',
  },
  {
    href: '/tools/tableau-to-sigma-formula-translator',
    label: 'Tableau to Sigma formula translator',
    body: 'Paste a calculated field and get the Sigma formula, with a plan for LOD expressions and table calculations.',
    tag: 'Migration',
  },
  {
    href: '/tools/dax-to-sigma-formula-translator',
    label: 'DAX to Sigma formula translator',
    body: 'Paste a Power BI measure and get the Sigma formula, with a plan for CALCULATE and time intelligence.',
    tag: 'Migration',
  },
  {
    href: '/tools/retention-value-calculator',
    label: 'Retention value calculator',
    body: 'See what a better 90-day repeat rate is worth in revenue and gross profit, and what each point is worth.',
    tag: 'Calculator',
  },
  {
    href: '/tools/reporting-time-calculator',
    label: 'Reporting time calculator',
    body: 'Estimate the hours and cost of building and reconciling reports by hand, and how long automating them would take to pay back.',
    tag: 'Calculator',
  },
  {
    href: '/tools/sigma-migration-readiness',
    label: 'Sigma migration readiness check',
    body: 'Sixteen questions across scope, data, acceptance, and rollout. See which gaps to close before asking anyone for a migration estimate.',
    tag: 'Checklist',
  },
];

const downloads = [
  { href: '/downloads/ecommerce-data-stack-white-paper.pdf', label: 'The Ecommerce and Consumer Brand Data Stack', note: 'White paper (PDF): tool selection by stage and a five-phase plan' },
  { href: '/downloads/customer-retention-reporting-white-paper.pdf', label: 'Customer Retention Reporting for Consumer Brands', note: 'White paper (PDF)' },
  { href: '/resources/sigma-migration-checklist.md', label: 'Sigma migration assessment checklist', note: 'Markdown worksheet' },
  { href: '/downloads/excel-to-sigma-functions/sales.csv', label: 'Sigma formula practice data', note: 'CSV, used in the Excel and Tableau cheat sheets' },
  { href: '/downloads/bi-sigma-formulas/orders.csv', label: 'DAX to Sigma practice data', note: 'CSV, used in the DAX cheat sheet' },
  { href: '/downloads/sigma-guides-expanded/validation-checklist.csv', label: 'Formula validation checklist', note: 'CSV of checks to run after a rebuild' },
  { href: '/downloads/migration-sample/README.txt', label: 'Migration validation sample', note: 'Orders, expected results, and acceptance cases' },
];

export default function ResourcesIndex() {
  const biCount = comparisons.filter((page) => page.group === 'bi').length;
  const ecommCount = comparisons.filter((page) => page.group === 'ecomm').length;

  return (
    <BlogLayout wide>
      <SEO
        title="Snowflake, Sigma, and ecommerce reporting resources"
        description="Topic guides, free tools, comparisons, and downloadable practice data for teams working with Snowflake, Sigma, and ecommerce reporting."
        url={`${SITE_URL}/resources`}
      />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
        ]}
      />

      <header className="rounded-[2rem] bg-[radial-gradient(circle_at_top_right,_#18356d_0,_#0a192f_42%,_#071525_100%)] px-6 py-12 text-white sm:px-10 sm:py-16 lg:px-14">
        <h1 className="max-w-3xl text-4xl font-semibold leading-[1.1] tracking-tight sm:text-6xl">Resources</h1>
        <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">Reading paths, free tools, and practice data for the work we do most: Snowflake setup and cost, Sigma builds and migrations, and ecommerce numbers that agree.</p>
      </header>

      <section aria-labelledby="topics-heading" className="py-12 sm:py-16">
        <h2 id="topics-heading" className="text-3xl font-semibold tracking-tight text-slate-950">Guides by topic</h2>
        <p className="mt-2 max-w-2xl text-slate-600">Each topic puts our articles in the order you would need them, with a note on when each one helps.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {topics.map((topic) => {
            const count = postsTagged(topic.tag).length;
            return (
              <Link key={topic.slug} to={`/resources/${topic.slug}`} className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
                <img src={topic.art} alt="" width="1440" height="480" loading="lazy" decoding="async" className="aspect-[3/1] w-full bg-slate-950 object-cover" />
                <span className="flex flex-1 flex-col p-6">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#1a4e94]">{count} articles</span>
                  <span className="mt-3 text-xl font-semibold text-slate-950 group-hover:text-[#1a4e94]">{topic.title}</span>
                  <span className="mt-2 text-sm leading-6 text-slate-600">{topic.description}</span>
                  <span className="mt-auto pt-5 text-sm font-semibold text-[#1a4e94]">Browse the guides <span aria-hidden="true">→</span></span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="tools-heading" className="border-t border-slate-200 py-12 sm:py-16">
        <h2 id="tools-heading" className="text-3xl font-semibold tracking-tight text-slate-950">Free tools</h2>
        <p className="mt-2 max-w-2xl text-slate-600">They run in your browser. Nothing you enter is sent anywhere. For a Sigma retention dashboard, a revenue goal calculator, and a barcode scanner, see the <Link to="/demos" className="font-semibold text-[#1a4e94] underline underline-offset-4">demos</Link>.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {tools.map((tool) => (
            <Link key={tool.href} to={tool.href} className="group rounded-3xl border border-slate-200 bg-slate-50 p-7 transition hover:border-blue-200 hover:bg-white hover:shadow-lg sm:p-8">
              <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-[#1a4e94]">{tool.tag}</span>
              <span className="mt-4 block text-2xl font-semibold text-slate-950 group-hover:text-[#1a4e94]">{tool.label}</span>
              <span className="mt-3 block leading-7 text-slate-600">{tool.body}</span>
              <span className="mt-6 inline-block font-semibold text-[#1a4e94]">Open the tool <span aria-hidden="true">→</span></span>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="compare-heading" className="grid gap-10 border-t border-slate-200 py-12 sm:py-16 lg:grid-cols-2">
        <div>
          <h2 id="compare-heading" className="text-3xl font-semibold tracking-tight text-slate-950">Comparisons</h2>
          <p className="mt-3 leading-7 text-slate-600">Side-by-side pages with a source link for every claim, including where the other option is the better choice.</p>
          <ul className="mt-6 space-y-3">
            <li><Link to="/compare" className="font-semibold text-[#1a4e94] underline underline-offset-4">Sigma vs other BI tools</Link> <span className="text-slate-500">({biCount} pages)</span></li>
            <li><Link to="/compare" className="font-semibold text-[#1a4e94] underline underline-offset-4">Custom warehouse vs packaged ecommerce reporting</Link> <span className="text-slate-500">({ecommCount} pages)</span></li>
          </ul>
        </div>
        <div>
          <h2 className="text-3xl font-semibold tracking-tight text-slate-950">Downloads</h2>
          <p className="mt-3 leading-7 text-slate-600">Worksheets and sample data used in the guides.</p>
          <ul className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
            {downloads.map((item) => (
              <li key={item.href} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3">
                <a href={item.href} download className="font-semibold text-[#1a4e94] hover:text-slate-950">{item.label}</a>
                <span className="text-sm text-slate-500">{item.note}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="rounded-3xl bg-slate-950 px-6 py-10 text-white sm:px-10 sm:py-12">
        <div className="flex flex-col justify-between gap-7 lg:flex-row lg:items-center">
          <div>
            <h2 className="max-w-2xl text-3xl font-semibold tracking-tight">Prefer to see finished work?</h2>
            <p className="mt-3 max-w-2xl text-slate-300">Case studies describe the problem, what we built, and the stack we used.</p>
          </div>
          <Link to="/case-studies" className="inline-flex shrink-0 items-center justify-center rounded-xl bg-white px-6 py-3 font-semibold text-slate-950 transition hover:bg-sky-100">Read the case studies <span aria-hidden="true" className="ml-2">→</span></Link>
        </div>
      </section>
    </BlogLayout>
  );
}
